import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Divider, IconButton, LinearProgress,
  Paper, Stack, Table, TableBody, TableCell, TableRow, Tooltip, Typography,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SaveIcon from '@mui/icons-material/Save';
import TerminalIcon from '@mui/icons-material/Terminal';
import ReplayIcon from '@mui/icons-material/Replay';

import api from '../../../../services/api';
import ColetaDoHost from './ColetaDoHost';
import { SUPERFICIE } from './estilo';

const ROTA = '/admin/snapshot';

/** mm:ss (ou h:mm:ss quando passa da hora). */
function fmtDuracao(ms) {
  if (ms == null || ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const seg = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(seg)}` : `${pad(m)}:${pad(seg)}`;
}

/** Contador rotulado. Os dois estágios da coleta precisam de nome próprio. */
function Contador({ icone, cor, rotulo, valor, total, ajuda }) {
  return (
    <Tooltip title={ajuda}>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Box sx={{ color: cor, display: 'flex' }}>{icone}</Box>
        <Typography variant="caption" sx={{ color: '#475569' }}>
          {rotulo}
        </Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: cor }}>
          {valor}/{total}
        </Typography>
      </Stack>
    </Tooltip>
  );
}

/** Um host, expansível para os comandos coletados. */
function LinhaHost({ h, host, saidas, runId, empresa, podeExpandir, onRetentar, retentando }) {
  const [aberto, setAberto] = useState(false);

  const erroExec = (saidas || []).filter((s) => s.exec_error).length;

  const cor = h.step === 'error' ? '#c0392b' : h.step === 'done' ? '#16a085' : '#64748b';
  const rotuloEtapa =
    h.step === 'done' ? 'coletado' : h.step === 'error' ? 'falhou' : h.step;

  return (
    <>
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 1,
          borderLeft: `3px solid ${cor}`, bgcolor: '#fafafa', mb: 0.5,
          cursor: podeExpandir ? 'pointer' : 'default',
        }}
        onClick={() => podeExpandir && setAberto((v) => !v)}
      >
        {podeExpandir && (
          <IconButton size="small" sx={{ p: 0.25 }}>
            {aberto ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
        )}
        <Typography sx={{ fontSize: 13, fontWeight: 600, minWidth: 150 }}>
          {host?.host_name || `Host ${h.host_id}`}
        </Typography>
        <Typography variant="caption" sx={{ color: '#64748b', minWidth: 110 }}>
          {host?.ip_address || ''}
        </Typography>

        <Chip size="small" label={rotuloEtapa}
          sx={{ height: 19, bgcolor: cor, color: '#fff' }} />

        {/* "gravado" é o segundo estágio: o worker terminou, mas o consumidor
            ainda pode não ter escrito no banco. Sem esse rótulo, a contagem do
            cabeçalho parece contradizer o chip acima. */}
        {h.persisted ? (
          <Chip size="small" label="gravado"
            sx={{ height: 19, bgcolor: '#2563eb', color: '#fff' }} />
        ) : (
          <Chip size="small" variant="outlined" label="gravando…"
            sx={{ height: 19, borderColor: '#94a3b8', color: '#64748b' }} />
        )}

        {erroExec > 0 && (
          <Tooltip title="Comandos que falharam neste host">
            <Chip size="small" label={`${erroExec} cmd com erro`}
              sx={{ height: 19, bgcolor: '#d97706', color: '#fff' }} />
          </Tooltip>
        )}

        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" sx={{ color: '#64748b' }}>
          {h.message || ''}
        </Typography>

        {/* Retentar preenche a lacuna DESTA coleta em vez de criar outra: um
            host que não respondeu é um buraco neste snapshot, não um snapshot
            novo. Também serve para conferir um equipamento sozinho. */}
        {onRetentar && (
          <Tooltip title={h.failed
            ? 'Coletar de novo só neste host, preenchendo a falha desta coleta'
            : 'Coletar de novo só neste host'}>
            <span onClick={(e) => e.stopPropagation()}>
              <Button
                size="small"
                variant={h.failed ? 'contained' : 'text'}
                color={h.failed ? 'error' : 'primary'}
                startIcon={<ReplayIcon sx={{ fontSize: 15 }} />}
                onClick={() => onRetentar([h.host_id])}
                disabled={retentando}
              >
                {h.failed ? 'retentar' : 'coletar'}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      <Collapse in={aberto} unmountOnExit>
        <Box sx={{ pl: 4, pr: 1, pb: 1.5 }}>
          <ColetaDoHost runId={runId} hostId={h.host_id} empresa={empresa} />
        </Box>
      </Collapse>
    </>
  );
}

/**
 * Painel de uma coleta: em andamento e depois de concluída.
 *
 * Continuar visível após a conclusão não é detalhe estético. Antes, o painel era
 * condicionado a "ainda não terminou" e portanto EVAPORAVA no instante em que a
 * coleta acabava — a conclusão era comunicada por desaparecimento, o que lê como
 * "sumiu do nada" em vez de "terminou".
 *
 * O painel também separa os dois estágios com nome próprio. O worker Go executa
 * os comandos e publica no Redis; um consumidor Python, em outro processo e com
 * varredura a cada 5s, grava no banco. São relógios diferentes: dá para ter
 * 4 hosts "coletados" e 0 "gravados" por alguns segundos, e mostrar os dois
 * números sem rótulo faz a tela parecer que está mentindo.
 */
export default function PainelColeta({ progresso, hostsPorId, empresa, iniciadoEm, rotulo, onRetentar, retentando }) {
  const [saidasPorHost, setSaidasPorHost] = useState({});
  const [carregandoSaidas, setCarregandoSaidas] = useState(false);
  const [agora, setAgora] = useState(Date.now());

  const duracaoFinal = useRef(null);

  const concluido = progresso?.status === 'done';
  const comErro = progresso?.status === 'error';
  const terminal = concluido || comErro;

  // Congela o cronômetro no instante em que o run vira terminal — senão ele
  // continuaria correndo depois de pronto, sugerindo trabalho que não existe.
  useEffect(() => {
    if (terminal && duracaoFinal.current == null && iniciadoEm) {
      duracaoFinal.current = Date.now() - iniciadoEm;
    }
    if (!terminal) duracaoFinal.current = null;
  }, [terminal, iniciadoEm]);

  useEffect(() => {
    if (terminal || !iniciadoEm) return undefined;
    const t = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(t);
  }, [terminal, iniciadoEm]);

  const decorrido = duracaoFinal.current != null
    ? duracaoFinal.current
    : iniciadoEm ? agora - iniciadoEm : null;

  const carregarSaidas = useCallback(async () => {
    if (!progresso?.run_id || !empresa) return;
    setCarregandoSaidas(true);
    try {
      const res = await api.get(`${ROTA}/run/${progresso.run_id}/outputs`, {
        params: { empresa },
      });
      const porHost = {};
      (res.data?.data || []).forEach((o) => {
        (porHost[o.host] = porHost[o.host] || []).push(o);
      });
      setSaidasPorHost(porHost);
    } catch {
      /* o painel continua útil sem o detalhe; o resumo vem do status */
    } finally {
      setCarregandoSaidas(false);
    }
  }, [progresso?.run_id, empresa]);

  // Busca o detalhe quando termina. Durante a coleta não vale a pena: as linhas
  // ainda estão chegando e o resultado mudaria a cada poll.
  useEffect(() => {
    if (terminal) carregarSaidas();
  }, [terminal, carregarSaidas]);

  const hosts = progresso?.hosts || [];
  const total = progresso?.total_hosts || 0;
  const executados = hosts.filter((h) => h.step === 'done' || h.step === 'error').length;
  const falhos = hosts.filter((h) => h.step === 'error').length;
  const gravados = progresso?.persisted_hosts || 0;

  const totalComandos = useMemo(
    () => Object.values(saidasPorHost).reduce((a, l) => a + l.length, 0),
    [saidasPorHost]
  );
  const comandosComErro = useMemo(
    () => Object.values(saidasPorHost).flat().filter((s) => s.exec_error).length,
    [saidasPorHost]
  );

  if (!progresso) return null;

  const pct = total ? Math.round((gravados / total) * 100) : 0;
  const pctExec = total ? Math.round((executados / total) * 100) : 0;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, ...SUPERFICIE }}>
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Coleta {rotulo} — #{progresso.run_id}
        </Typography>

        <Chip
          size="small"
          icon={terminal
            ? (comErro ? <ErrorOutlineIcon sx={{ fontSize: 15 }} /> : <CheckCircleIcon sx={{ fontSize: 15 }} />)
            : undefined}
          label={comErro ? 'com erro' : concluido ? 'concluída' : 'em andamento'}
          sx={{
            height: 21, color: '#fff',
            bgcolor: comErro ? '#c0392b' : concluido ? '#16a085' : '#2563eb',
            '& .MuiChip-icon': { color: '#fff' },
          }}
        />

        {/* O cronômetro responde "está andando ou travou?", que é a pergunta real
            de quem olha uma coleta demorada. */}
        <Tooltip title={terminal ? 'Tempo total da coleta' : 'Tempo desde o disparo'}>
          <Typography
            sx={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600, color: '#334155' }}
          >
            {terminal ? 'levou ' : ''}{fmtDuracao(decorrido)}
          </Typography>
        </Tooltip>

        <Box sx={{ flex: 1 }} />

        <Contador
          icone={<TerminalIcon sx={{ fontSize: 16 }} />} cor="#16a085"
          rotulo="coletados" valor={executados} total={total}
          ajuda="Hosts em que o worker já executou os comandos."
        />
        <Contador
          icone={<SaveIcon sx={{ fontSize: 16 }} />} cor="#2563eb"
          rotulo="gravados" valor={gravados} total={total}
          ajuda="Hosts já gravados no banco. A gravação roda em outro processo e pode levar alguns segundos depois da coleta — é normal este número ficar atrás do anterior."
        />
      </Stack>

      {/* As barras só existem enquanto há o que acompanhar.
          Depois de concluída elas ficavam as duas cheias e VERMELHAS — o
          `primary` deste tema é #e01b29, a cor da marca — o que lia como falha
          justamente no caso de sucesso. E duas barras 100% lado a lado não
          informam nada que os contadores acima já não digam. */}
      {!terminal && (
        <Box sx={{ mt: 1.5, mb: 1 }}>
          <LinearProgress
            variant="determinate"
            value={pctExec}
            color="info"
            sx={{ height: 5, borderRadius: 1, mb: 0.5 }}
          />
          <LinearProgress
            variant="determinate"
            value={pct}
            color="info"
            sx={{ height: 5, borderRadius: 1 }}
          />
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
            barra de cima: execução nos equipamentos · barra de baixo: gravação no banco
          </Typography>
        </Box>
      )}

      {terminal && (
        <Alert
          severity={comErro || falhos ? 'warning' : 'success'}
          sx={{ mb: 1.5 }}
        >
          {falhos > 0 ? (
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <Box>
                Coleta encerrada em {fmtDuracao(decorrido)} com <strong>{falhos} host(s) sem
                resposta</strong>. Uma comparação feita assim cobre só os hosts que responderam.
              </Box>
              {onRetentar && (
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  startIcon={<ReplayIcon sx={{ fontSize: 16 }} />}
                  onClick={() => onRetentar(null)}
                  disabled={retentando}
                >
                  {retentando ? 'retentando…' : `retentar os ${falhos}`}
                </Button>
              )}
            </Stack>
          ) : (
            <>
              Coleta concluída em {fmtDuracao(decorrido)}: {total} host(s)
              {totalComandos ? `, ${totalComandos} comando(s)` : ''}
              {comandosComErro ? `, ${comandosComErro} com erro` : ', tudo ok'}.
            </>
          )}
        </Alert>
      )}

      <Divider sx={{ mb: 1 }} />

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
        <Typography variant="caption" sx={{ color: '#475569' }}>
          {terminal
            ? 'Clique num host para ver os comandos e a saída bruta'
            : 'Hosts desta coleta'}
        </Typography>
        {carregandoSaidas && <CircularProgress size={12} />}
      </Stack>

      {hosts.map((h) => (
        <LinhaHost
          key={h.host_id}
          h={h}
          host={hostsPorId[h.host_id]}
          saidas={saidasPorHost[h.host_id]}
          runId={progresso.run_id}
          empresa={empresa}
          podeExpandir={terminal}
          onRetentar={onRetentar}
          retentando={retentando}
        />
      ))}
    </Paper>
  );
}
